import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Pagination,
  TextField,
  Typography,
  Alert,
  Collapse,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Search,
  ExpandMore,
  ExpandLess,
  FilterList,
  DateRange,
  Attachment
} from '@mui/icons-material';
import DOMPurify from 'dompurify';

const EmailAuditDashboard = () => {
  const [searchParams, setSearchParams] = useState({
    from: '',
    to: '',
    date: '',
    page: 1,
    pageSize: 10
  });
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [suggestions, setSuggestions] = useState({
    from: [],
    to: []
  });

  const fetchSuggestions = async (type, query) => {
    try {
      const response = await axios.get(`/api/emails/suggestions?type=${type}&query=${query}`);
      setSuggestions(prev => ({ ...prev, [type]: response.data }));
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({ ...prev, [name]: value }));
    
    if (name === 'from' || name === 'to') {
      if (value.length > 2) fetchSuggestions(name, value);
    }
  };

  const fetchEmails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/api/emails/get/`, {
        params: {
          from: searchParams.from,
          to: searchParams.to,
          date: searchParams.date,
          page: searchParams.page,
          pageSize: searchParams.pageSize
        }
      });
      
      setEmails(response.data?.emails || []);
      setTotalCount(response.data?.totalCount || 0);
    } catch (err) {
      setError('Failed to fetch emails. Please try again.');
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchParams.from.length > 2) fetchSuggestions('from', searchParams.from);
      if (searchParams.to.length > 2) fetchSuggestions('to', searchParams.to);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchParams.from, searchParams.to]);

  const handlePageChange = (event, page) => {
    setSearchParams(prev => ({ ...prev, page }));
    fetchEmails();
  };

  const toggleExpand = (emailId) => {
    setExpandedEmail(expandedEmail === emailId ? null : emailId);
  };
  const sanitizeHTML = (html) => ({
    __html: DOMPurify.sanitize(
      html
        ? html
            // Replace tracking URLs inside anchor tags (`<a href="..."`)
            .replace(
              /<a\s+[^>]*href=["']?https:\/\/vibrant-catbird-399009\.el\.r\.appspot\.com\/click\/\S+\?url=([^"&]+)[^>]*>(.*?)<\/a>/gi,
              (match, p1, linkText) =>
                `<a href="${decodeURIComponent(p1)}" target="_blank" style="color: #1e88e5;">${linkText}</a>`
            )
            // Remove tracking pixel images
            .replace(
              /<img[^>]+src=["']?https:\/\/vibrant-catbird-399009\.el\.r\.appspot\.com\/images\/\S+\.png["']?[^>]*>/gi,
              ""
            )
        : "<p>No email body available.</p>"
    )
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
        Email Audit Dashboard
      </Typography>

      {/* Search Filters */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={suggestions.from}
                onInputChange={(e, value) => setSearchParams(prev => ({ ...prev, from: value }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sender"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <FilterList />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={suggestions.to}
                onInputChange={(e, value) => setSearchParams(prev => ({ ...prev, to: value }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Recipient"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <FilterList />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                name="date"
                value={searchParams.date}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRange />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchEmails}
                startIcon={<Search />}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Status Indicators */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Email List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {emails?.map((email) => (
          <Card key={email._id} variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">{email.subject}</Typography>
                <IconButton onClick={() => toggleExpand(email._id)}>
                  {expandedEmail === email._id ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6} md={2}>
                  <Chip 
                    label={email.status} 
                    color={
                      email.status === 'Sent' ? 'success' : 
                      email.status === 'Failed' ? 'error' : 'warning'
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  {email.attachments?.length > 0 && (
                    <Chip
                      icon={<Attachment fontSize="small" />}
                      label={`${email.attachments.length} files`}
                      size="small"
                    />
                  )}
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption">From: {email.from}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption">To: {email.to}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption">
                    Sent: {new Date(email.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>

              <Collapse in={expandedEmail === email._id}>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Email Content:
                  </Typography>
                  <Box 
                    sx={{ 
                      '& img': { maxWidth: '100%' },
                      '& table': { borderCollapse: 'collapse', width: '100%' },
                      '& td, & th': { border: '1px solid #ddd', padding: '8px' }
                    }}
                    dangerouslySetInnerHTML={sanitizeHTML(email.body)}
                  />

                  {email.links?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Tracked Links:
                      </Typography>
                      {email.links.map((link, index) => (
                        <div key={index}>
                          <Typography variant="caption">Original: {link.original}</Typography>
                          <Typography variant="caption" display="block">
                            Tracking: {link.tracking}
                          </Typography>
                        </div>
                      ))}
                    </Box>
                  )}

                  {email.attachments?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Attachments:
                      </Typography>
                      {email.attachments.map((file, index) => (
                        <Chip
                          key={index}
                          icon={<Attachment />}
                          label={file.name}
                          onClick={() => window.open(file.url, '_blank')}
                          sx={{ mr: 1, mb: 1 }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Pagination */}
      {totalCount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(totalCount / searchParams.pageSize)}
            page={searchParams.page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {!loading && emails?.length === 0 && (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 3 }}>
          No emails found matching your criteria
        </Typography>
      )}
    </Box>
  );
};

export default EmailAuditDashboard;